/* =====================================================================
 * WorkspaceSSE — fetch 기반 SSE 클라이언트
 *
 * 네이티브 EventSource는 커스텀 헤더를 지원하지 않아 JWT가 URL에
 * 노출됩니다. 대신 fetch() + ReadableStream을 사용하여
 * Authorization 헤더를 정상 전송합니다.
 * ===================================================================== */

import {
  ensureFreshToken,
  API_BASE_URL,
  getApiKey,
  hasAuthSessionContext,
  logoutActiveAccount,
  refreshAuthSession,
} from './authFetch';
import { dispatchAuthSessionInvalidated } from '@auw/workspace-utils';

const WS_PREFIX = `${API_BASE_URL}/api/v1/workspace`;

/** 현재 탭 고유 ID — 페이지 로드마다 생성 */
export const TAB_ID: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export interface WorkspaceSSEvent {
  type: string;       // 'item_created' | 'item_updated' | 'item_deleted' | 'item_moved'
  item_id?: string;
  source_tab?: string;
  data?: unknown;
}

type EventHandler = (event: WorkspaceSSEvent) => void;
type ReconnectHandler = () => void;

const INITIAL_RETRY_MS = 2_000;
const MAX_RETRY_MS = 30_000;

class WorkspaceSSEClient {
  private abortController: AbortController | null = null;
  private retryMs = INITIAL_RETRY_MS;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private handlers: EventHandler[] = [];
  private reconnectHandlers: ReconnectHandler[] = [];
  private _connected = false;
  private _wasConnected = false;
  private _connecting = false;
  private _connectAttemptId = 0;
  private _consecutiveFailures = 0;

  /** 이벤트 핸들러 등록. 해제 함수를 반환합니다. */
  onEvent(handler: EventHandler) {
    this.handlers.push(handler);
    return () => { this.handlers = this.handlers.filter((h) => h !== handler); };
  }

  /** 재연결 완료 핸들러 등록. 해제 함수를 반환합니다. */
  onReconnect(handler: ReconnectHandler) {
    this.reconnectHandlers.push(handler);
    return () => { this.reconnectHandlers = this.reconnectHandlers.filter((h) => h !== handler); };
  }

  /** SSE 연결 시작 */
  connect() {
    if (this.abortController || this._connecting) return;
    const attemptId = ++this._connectAttemptId;
    this._connecting = true;

    void (async () => {
      let ac: AbortController | null = null;
      let skipReconnect = false;
      let reconnectImmediately = false;
      try {
        const token = await ensureFreshToken();
        if (attemptId !== this._connectAttemptId) return;
        if (!token) {
          this.scheduleReconnect();
          return;
        }

        ac = new AbortController();
        if (attemptId !== this._connectAttemptId) return;
        this.abortController = ac;

        const headers: Record<string, string> = {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          'X-Tab-ID': TAB_ID,
        };
        const apiKey = getApiKey();
        if (apiKey) headers['X-API-Key'] = apiKey;

        const res = await fetch(`${WS_PREFIX}/events`, {
          headers,
          signal: ac.signal,
        });
        if (attemptId !== this._connectAttemptId) return;
        if (res.status === 401) {
          const recovered = await refreshAuthSession();
          if (attemptId !== this._connectAttemptId) return;
          if (recovered) {
            reconnectImmediately = true;
            return;
          }
          skipReconnect = true;
          logoutActiveAccount();
          /* Phase 11-3a: tenant-specific landing 은 App.tsx 가 처리한다.
           * workspace SSE 레이어는 reason 만 전달. */
          dispatchAuthSessionInvalidated({
            reason: 'workspace_sse_401',
          });
          return;
        }
        if (!res.ok || !res.body) {
          throw new Error(`SSE HTTP ${res.status}`);
        }

        const isReconnect = this._wasConnected;
        this._connected = true;
        this._wasConnected = true;
        this._consecutiveFailures = 0;
        this.retryMs = INITIAL_RETRY_MS;
        console.debug('[WorkspaceSSE] 연결됨', isReconnect ? '(재연결)' : '(최초)');

        if (isReconnect) {
          for (const handler of this.reconnectHandlers) handler();
        }

        await this.readStream(res.body);
      } catch (err) {
        if (err && typeof err === 'object' && 'name' in err && err.name === 'AbortError') return;
        this.reportReconnectableFailure('연결 실패', err);
      } finally {
        this._connecting = false;
        if (ac && this.abortController === ac) {
          this.cleanup();
          if (reconnectImmediately) {
            void this.connect();
            return;
          }
          if (!skipReconnect) {
            this.scheduleReconnect();
          }
        }
      }
    })();
  }

  /** SSE 연결 종료 */
  disconnect() {
    this._connectAttemptId += 1;
    this._connecting = false;
    this.cleanup();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  get isConnected() {
    return this._connected;
  }

  /* ---------------------------------------------------------------- */

  /**
   * ReadableStream에서 SSE 프레임을 파싱합니다.
   * 형식: "data: {...}\n\n" 또는 ": keepalive\n\n"
   */
  private async readStream(body: ReadableStream<Uint8Array>) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          if (!frame.trim() || frame.startsWith(':')) continue;

          const dataLine = frame
            .split('\n')
            .find((line) => line.startsWith('data: '));
          if (!dataLine) continue;

          try {
            const evt: WorkspaceSSEvent = JSON.parse(dataLine.slice(6));
            if (evt.source_tab === TAB_ID) continue;
            for (const handler of this.handlers) handler(evt);
          } catch {
            /* 파싱 불가 무시 */
          }
        }
      }
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        this.reportReconnectableFailure('스트림 읽기 오류', err);
      }
    } finally {
      reader.releaseLock();
    }
  }

  private cleanup() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this._connected = false;
  }

  private scheduleReconnect() {
    if (!hasAuthSessionContext()) return;
    if (this.retryTimer) return;
    console.debug(`[WorkspaceSSE] ${this.retryMs}ms 후 재연결 시도`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, this.retryMs);
    this.retryMs = Math.min(this.retryMs * 2, MAX_RETRY_MS);
  }

  private reportReconnectableFailure(context: string, err: unknown) {
    this._consecutiveFailures += 1;
    if (this._wasConnected && this._consecutiveFailures === 1) {
      console.debug(`[WorkspaceSSE] ${context} — 재연결 대기`);
      return;
    }
    console.warn(`[WorkspaceSSE] ${context}:`, err);
  }

}

/** 앱 전역 싱글턴 인스턴스 */
export const workspaceSSE = new WorkspaceSSEClient();
